import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  filter,
  finalize,
  from,
  map,
  Observable,
  of,
  throwError,
} from "rxjs";
import { IDBPDatabase, openDB } from "idb";
import { switchMap } from "rxjs/operators";
import { IDataResponse } from "@app/modules/registration/registration-card/models/card.model";
import { HttpService } from "@app/core/services/http.service";

@Injectable({
  providedIn: 'root'
})
export class FieldsCacheService {

  private dbSubject: BehaviorSubject<IDBPDatabase<any> | null>;

  constructor(
    private httpService: HttpService,
  ) {
    this.dbSubject = new BehaviorSubject<IDBPDatabase<any> | null>(null);

    openDB('fields-cache-db', 1, {
      upgrade(db) {
        db.createObjectStore('fields', { keyPath: 'id' });
      },
    }).then(db => {
      this.dbSubject.next(db);
    }).catch(error => {
      console.error('Failed to open IndexedDB:', error);
    });
  }

  private getDatabase(): Observable<IDBPDatabase<any>> {
    return this.dbSubject.asObservable().pipe(
      filter((db): db is IDBPDatabase<any> => !!db)
    );
  }

  cacheFields(id: string, data: any): Observable<void> {
    return this.getDatabase().pipe(
      switchMap(db => from(db.put('fields', { id, data })))
    );
  }

  getCachedFields(id: string): Observable<any | null> {
    return this.getDatabase().pipe(
      switchMap(db => from(db.get('fields', id))),
      switchMap(result => of(result ? result.data : null))
    );
  }

  getFields(model: string): Observable<any> {
    return this.getCachedFields(model).pipe(
      switchMap((cachedFields) => {
        if (cachedFields) {
          return of(cachedFields);
        } else {
          return this.getFieldsByModel(model).pipe(
            map((data) => {
              if (!data) {
                return null;
              }
              const perms = { perms: data.perms };
              const fields = this.processFields(data.fields);
              return { ...fields, ...perms };
            }),
            switchMap((fields) =>
              this.cacheFields(model, fields).pipe(
                map(() => fields)
              )
            )
          );
        }
      }),
      catchError((error) => {
        console.error('Ошибка при загрузке полей:', error);
        return of([]);
      })
    );
  }

  private processFields(viewData: any): any {
    const viewItems: any = {};

    viewData.forEach((field: any) => {
      viewItems[field.name] = {
        ...field
      };
      delete viewItems[field.name].name;
      if (field.selectionList && Array.isArray(field.selectionList)) {
        viewItems[field.name].selectionList = field.selectionList.map((item: any) => ({
          title: item.title,
          value: item.value
        }));
      }
    });

    return viewItems;
  }

  getFieldsByModel(model: string): Observable<any> {
    return this.httpService.getModelProperties<IDataResponse>(model).pipe(
      map(data => {
        if (data.status === -1) {
          return null;
        }
        return data.data;
      }),
      catchError(error => throwError(() => error))
    );
  }

  deleteFields(id: string): Observable<void> {
    return this.getDatabase().pipe(
      switchMap(db => from(db.delete('fields', id)))
    );
  }

  clearCache(): Observable<void> {
    return this.getDatabase().pipe(
      switchMap(db => from(db.clear('fields')))
    );
  }

  deleteOldFields(): Observable<void> {
    const expirationDate = new Date(Date.now() - 5 * 24 * 60 * 60 * 1000); // 5 дней назад

    return this.getDatabase().pipe(
      switchMap(db => {
        const transaction = db.transaction('fields', 'readwrite');
        const store = transaction.objectStore('fields');
        const request = store.openCursor();

        return from(request).pipe(
          switchMap(function processCursor(cursor): Observable<void> {
            if (!cursor) return of(undefined) as Observable<void>;

            const record = cursor.value;
            const recordDate = new Date(record.timestamp);

            let delete$ = of(undefined) as Observable<void>;
            if (recordDate < expirationDate) {
              delete$ = from(cursor.delete()).pipe(
                catchError(err => {
                  console.error('Failed to delete record:', err);
                  return of(undefined) as Observable<void>;
                })
              );
            }

            return delete$.pipe(
              switchMap(() => from(cursor.continue())),
              switchMap(cursor => processCursor(cursor))
            );
          }),
          finalize(() => transaction.done)
        );
      }),
      catchError(error => {
        console.error('Failed to delete old fields:', error);
        return of(undefined) as Observable<void>;
      })
    );
  }

  runDailyCleanup(): void {
    const lastCleanup = localStorage.getItem('lastFieldsCleanup');
    const now = Date.now();

    if (!lastCleanup || now - parseInt(lastCleanup, 10) > 24 * 60 * 60 * 1000) {
      this.deleteOldFields().subscribe({
        next: () => {
          localStorage.setItem('lastFieldsCleanup', now.toString());
        },
        error: (error) => {
          console.error('Error while deleting old fields:', error);
        }
      });
    }
  }
}
